/**
 * Trading Signals Hook
 *
 * Manages trading signal filtering and state for Bot.tsx
 * Extracted to reduce component complexity
 */

import { useState, useMemo, useCallback } from 'react';

export type SignalType = 'buy' | 'sell' | 'hold';

export interface TradingSignal {
  tokenId: string;
  tokenName: string;
  action: SignalType;
  confidence: number;
  reason: string;
  currentPrice: number;
  suggestedPrice?: number;
  timeframe: string;
}

export interface SignalFilters {
  action: SignalType | 'all';
  confidence: number;
  token: string;
  timeframe: string;
}

export interface UseTradingSignalsReturn {
  filters: SignalFilters;
  setFilterAction: (action: SignalType | 'all') => void;
  setFilterConfidence: (confidence: number) => void;
  setFilterToken: (token: string) => void;
  setFilterTimeframe: (timeframe: string) => void;
  resetFilters: () => void;
  filterSignals: (signals: TradingSignal[]) => TradingSignal[];
}

const DEFAULT_FILTERS: SignalFilters = {
  action: 'all',
  confidence: 0,
  token: '',
  timeframe: 'all'
};

/**
 * Hook for managing trading signal filters
 *
 * @example
 * ```tsx
 * const { filters, filterSignals, resetFilters } = useTradingSignals();
 * const filtered = filterSignals(allSignals);
 * ```
 */
export function useTradingSignals(): UseTradingSignalsReturn {
  const [filters, setFilters] = useState<SignalFilters>(DEFAULT_FILTERS);

  const setFilterAction = useCallback((action: SignalType | 'all') => {
    setFilters(prev => ({ ...prev, action }));
  }, []);

  const setFilterConfidence = useCallback((confidence: number) => {
    setFilters(prev => ({ ...prev, confidence }));
  }, []);

  const setFilterToken = useCallback((token: string) => {
    setFilters(prev => ({ ...prev, token }));
  }, []);

  const setFilterTimeframe = useCallback((timeframe: string) => {
    setFilters(prev => ({ ...prev, timeframe }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const filterSignals = useCallback((signals: TradingSignal[]): TradingSignal[] => {
    return signals.filter(signal => {
      // Filter by action
      if (filters.action !== 'all' && signal.action !== filters.action) return false;

      // Filter by confidence
      if (signal.confidence < filters.confidence) return false;

      // Filter by token (case-insensitive search)
      if (filters.token && !signal.tokenName.toLowerCase().includes(filters.token.toLowerCase())) return false;

      // Filter by timeframe
      if (filters.timeframe !== 'all' && signal.timeframe !== filters.timeframe) return false;

      return true;
    });
  }, [filters]);

  return {
    filters,
    setFilterAction,
    setFilterConfidence,
    setFilterToken,
    setFilterTimeframe,
    resetFilters,
    filterSignals
  };
}

/**
 * Get signal action color
 */
export function getSignalColor(action: SignalType): string {
  switch (action) {
    case 'buy': return '#10b981';
    case 'sell': return '#ef4444';
    case 'hold': return '#f59e0b';
  }
}

/**
 * Get sentiment color
 */
export function getSentimentColor(sentiment: 'bullish' | 'neutral' | 'bearish'): string {
  switch (sentiment) {
    case 'bullish': return '#10b981';
    case 'neutral': return '#f59e0b';
    case 'bearish': return '#ef4444';
  }
}

/**
 * Normalize UI timeframe to API format
 */
export function normalizeTimeframe(timeframe: string): string {
  const lower = timeframe.toLowerCase();
  if (lower.includes('week')) return '1w';
  if (lower.includes('day')) return '1d';
  if (lower.includes('hour') || lower.includes('h')) return '1h';
  return '1d';
}
